import { AlertCircle, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export type ConnectionStatus = "connected" | "pending" | "error" | "unknown";

interface ConnectionStatusBadgeProps {
	status: ConnectionStatus;
	errorMessage?: string | null;
	label?: string;
}

export function ConnectionStatusBadge({
	status,
	errorMessage,
	label,
}: ConnectionStatusBadgeProps) {
	const statusConfig = {
		connected: {
			label: "Connected",
			color: "green",
			icon: CheckCircle2,
			variant: "outline" as const,
			className: "border-green-500 text-green-400 bg-green-950",
		},
		pending: {
			label: "Pending",
			color: "yellow",
			icon: Clock,
			variant: "outline" as const,
			className: "border-yellow-500 text-yellow-400 bg-yellow-950",
		},
		error: {
			label: "Error",
			color: "red",
			icon: XCircle,
			variant: "outline" as const,
			className: "border-red-500 text-red-400 bg-red-950",
		},
		unknown: {
			label: "Unknown",
			color: "yellow",
			icon: AlertCircle,
			variant: "outline" as const,
			className: "border-yellow-500 text-yellow-400 bg-yellow-950",
		},
	};

	const config = statusConfig[status] || statusConfig.unknown;
	const Icon = config.icon;
	const displayLabel = label || config.label;

	const badge = (
		<Badge
			variant={config.variant}
			className={`text-xs cursor-help flex items-center gap-1 ${config.className}`}
		>
			<Icon className="h-3 w-3" />
			{displayLabel}
		</Badge>
	);

	if (status === "error" && errorMessage) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>{badge}</TooltipTrigger>
				<TooltipContent>
					<p className="max-w-xs">{errorMessage}</p>
				</TooltipContent>
			</Tooltip>
		);
	}

	if (status === "connected") {
		return (
			<Tooltip>
				<TooltipTrigger asChild>{badge}</TooltipTrigger>
				<TooltipContent>
					<p>This configuration is connected and ready to use.</p>
				</TooltipContent>
			</Tooltip>
		);
	}

	if (status === "pending") {
		return (
			<Tooltip>
				<TooltipTrigger asChild>{badge}</TooltipTrigger>
				<TooltipContent>
					<p>Connection status is being verified.</p>
				</TooltipContent>
			</Tooltip>
		);
	}

	return badge;
}
